import React from 'react';
import { useCountUp } from '../../hooks/useCountUp';
import { ForgeStatCard } from '../../types';

interface StatCardProps {
  stat: ForgeStatCard;
  isVisible: boolean;
}

function getCountUpProps(stat: ForgeStatCard) {
  switch (stat.format) {
    case 'percent':
      return { target: stat.value, suffix: '%', decimals: 1 };
    case 'time':
      return { target: stat.value, suffix: 's', decimals: 1 };
    case 'decimal':
      return { target: stat.value, suffix: '', decimals: 2 };
    default:
      return { target: stat.value, suffix: '', decimals: 0 };
  }
}

function getDiffType(diff?: number): 'positive' | 'negative' | 'neutral' {
  if (!diff) return 'neutral';
  if (diff > 0) return 'positive';
  if (diff < 0) return 'negative';
  return 'neutral';
}

function formatDiff(diff?: number, label?: string): string | null {
  if (diff === undefined && !label) return null;
  const parts: string[] = [];
  if (diff !== undefined) {
    const sign = diff > 0 ? '+' : '';
    parts.push(`${sign}${diff}`);
  }
  if (label) parts.push(label);
  return parts.join(' ');
}

export const StatCard: React.FC<StatCardProps> = ({ stat, isVisible }) => {
  const props = getCountUpProps(stat);
  const display = useCountUp({
    ...props,
    enabled: isVisible,
  });

  const diffText = formatDiff(stat.diff, stat.diffLabel);
  const diffType = getDiffType(stat.diff);

  const classes = [
    'stat-card',
    stat.accent ? 'is-best' : '',
    isVisible ? 'is-visible' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      <div className="stat-label">{stat.label}</div>
      <div className="stat-value-row">
        <span className="stat-value">{display}</span>
      </div>
      {diffText && (
        <div className="stat-sub">
          <span className={`stat-diff ${diffType}`}>
            {stat.diff !== undefined && stat.diff > 0 ? '+' : ''}
            {stat.diff !== undefined ? stat.diff : ''}
          </span>
          {stat.diffLabel && (
            <span style={{ color: 'var(--text-tertiary)', marginLeft: 4 }}>{stat.diffLabel}</span>
          )}
        </div>
      )}
    </div>
  );
};
